import React, { useEffect, useState } from 'react';
import { api } from '../api.js';
import { useLang } from '../i18n.js';
import { thb, thbShort, shortDate, relTime } from '../money.js';
import { Card, Kpi, Empty, Spinner, Avatar, EntityGlyph, SplitBar } from '../ui.jsx';

/**
 * Today.
 *
 * Not a dashboard — a worklist. v2 opened on charts that told you how last
 * quarter went and nothing about what to do next. Every block here is something
 * you can act on, generated from the funnel rather than typed in by anyone.
 */
export default function Today({
  me,
  onOpenDeal,
  onOpenClient,
  onQuickLog,
  onGraduate,
  onLogCall,
  toast,
}) {
  const { t, lang } = useLang();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const load = () =>
    api
      .today()
      .then(setData)
      .catch((e) => setError(e.message));

  useEffect(() => {
    load();
  }, []);

  if (error) return <div className="page">{error}</div>;
  if (!data) return <Spinner label={t('loading')} />;

  const a = data.my_attainment;
  const quota = a.quota_satang || 0;
  // Scale both segments against whichever is larger, quota or actual. Clamping
  // each to the quota made an over-performing month render as one solid bar
  // with a sliver on the end — the split stops being readable exactly when the
  // person most wants to see it.
  const denom = Math.max(quota, a.actual_satang, 1);
  const newPct = (a.won_new_satang / denom) * 100;
  const upPct = (a.won_upsell_satang / denom) * 100;

  const completeTask = async (id) => {
    setData((d) => ({ ...d, tasks: d.tasks.filter((x) => x.id !== id) })); // optimistic
    try {
      await api.completeTask(id);
      toast(t('taskDone'));
    } catch (e) {
      toast(e.message);
      load();
    }
  };

  return (
    <div className="page">
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 16,
          marginBottom: 22,
        }}
      >
        <div>
          <div className="display" style={{ fontSize: 30 }}>
            {t('morning')}, {me.name.split(' ')[0]}
          </div>
          <div style={{ fontSize: 13.5, color: 'var(--ink-2)', marginTop: 4 }}>
            {new Date().toLocaleDateString(lang === 'th' ? 'th-TH' : 'en-GB', {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
              timeZone: 'Asia/Bangkok',
            })}{' '}
            · {me.role}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Kpi label={t('toGraduate')} value={data.to_graduate.length} />
          <Kpi label={t('needsAttention')} value={data.needs_attention.length} />
          <Kpi label={t('myTasks')} value={data.tasks.length} />
        </div>
      </div>

      <div className="grid-auto">
        {/* --------------------------------------------------------- attainment */}
        <Card style={{ gridColumn: '1/-1', padding: '16px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <span className="card-title">{t('myAttainment')}</span>
            <span style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>{data.period}</span>
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: 10,
              marginBottom: 10,
              flexWrap: 'wrap',
            }}
          >
            <span className="display num" style={{ fontSize: 32, lineHeight: 1 }}>
              {thb(a.actual_satang)}
            </span>
            <span style={{ fontSize: 13, color: 'var(--ink-3)', paddingBottom: 4 }}>
              {/* "No quota set" and "0% of quota" are different facts. The API
                  returns null rather than 0 precisely so we can say which. */}
              {a.pct === null ? t('noQuota') : t('ofQuota', { q: thbShort(quota) })}
            </span>
            {a.pct !== null && (
              <span
                className="display num"
                style={{
                  marginLeft: 'auto',
                  fontSize: 22,
                  paddingBottom: 2,
                  color: a.pct >= 100 ? 'var(--success)' : a.pct >= 60 ? 'var(--ink)' : 'var(--warning)',
                }}
              >
                {a.pct}%
              </span>
            )}
          </div>

          <SplitBar newPct={newPct} upPct={upPct} />
          <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 11.5 }}>
            <Legend color="var(--primary)" label={t('newCust')} value={thb(a.won_new_satang)} />
            <Legend color="var(--mr-cyan-deep)" label={t('upsell')} value={thb(a.won_upsell_satang)} />
          </div>
        </Card>

        {/* --------------------------------------------------------------- team */}
        <Card title={t('teamAttainment')} style={{ gridColumn: '1/-1' }}>
          {data.team_attainment.length === 0 ? (
            <Empty>—</Empty>
          ) : (
            data.team_attainment.map((p) => {
              const q = (p.quota_new_satang || 0) + (p.quota_upsell_satang || 0);
              const won = (p.won_new_satang || 0) + (p.won_upsell_satang || 0);
              const pct = q ? Math.round((won / q) * 100) : null;
              return (
                <div className="row" key={p.id}>
                  <Avatar initials={p.avatar_initials} seed={p.avatar_var} />
                  <span className="row-title" style={{ flex: 1 }}>
                    {p.name}
                  </span>
                  <span className="num" style={{ fontSize: 12.5 }}>
                    {thbShort(won)}
                  </span>
                  <span
                    className="num"
                    style={{
                      width: 52,
                      textAlign: 'right',
                      fontWeight: 700,
                      color: pct === null ? 'var(--ink-3)' : pct >= 100 ? 'var(--success)' : 'var(--ink-2)',
                    }}
                  >
                    {pct === null ? '—' : `${pct}%`}
                  </span>
                </div>
              );
            })
          )}
        </Card>

        {/* ------------------------------------------------------- to graduate */}
        <Card title={t('toGraduate')}>
          {data.to_graduate.length === 0 ? (
            <Empty>{t('nothingWaiting')}</Empty>
          ) : (
            data.to_graduate.map((d) => (
              <div className="row" key={d.id}>
                <EntityGlyph entity={d.entity_tag} />
                <span
                  style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
                  onClick={() => onOpenDeal(d.id)}
                >
                  <span className="row-title" style={{ display: 'block' }}>
                    {d.company_name}
                  </span>
                  <span style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
                    {d.title} · {thbShort(d.amount_satang)}
                  </span>
                </span>
                <button className="btn btn-sm btn-primary" onClick={() => onGraduate(d)}>
                  {t('graduate')}
                </button>
              </div>
            ))
          )}
        </Card>

        {/* ------------------------------------------------------------ briefs */}
        <Card title={t('newBriefs')}>
          {data.briefs.length === 0 ? (
            <Empty>{t('briefsZero')}</Empty>
          ) : (
            data.briefs.map((b) => (
              <div className="row" key={b.id}>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span className="row-title" style={{ display: 'block' }}>
                    {b.client_name || '—'}
                  </span>
                  <span style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
                    {b.campaign} · {relTime(b.submitted_at, lang)}
                  </span>
                </span>
                <button
                  className="btn btn-sm"
                  onClick={() =>
                    api
                      .createDealFromBrief(b.id)
                      .then((r) => {
                        toast(`${r.deal.code} created`);
                        load();
                      })
                      .catch((e) => toast(e.message))
                  }
                >
                  {t('createDeal')}
                </button>
              </div>
            ))
          )}
        </Card>

        {/* ------------------------------------------------------- calls due */}
        <Card title={t('callsDue')}>
          {data.calls_due.length === 0 ? (
            <Empty>{t('noCallsDue')}</Empty>
          ) : (
            data.calls_due.map((p) => (
              <div className="row" key={p.id}>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span className="row-title" style={{ display: 'block' }}>
                    {p.raw_name}
                  </span>
                  <span style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
                    {p.contact_name || '—'} · {p.phone || '—'}
                  </span>
                </span>
                <button
                  className="btn btn-sm"
                  onClick={() =>
                    onLogCall({
                      prospect_id: p.id,
                      label: p.raw_name,
                      attempts: p.attempts,
                      contact_name: p.contact_name,
                      phone: p.phone,
                    })
                  }
                >
                  {t('logCall')}
                </button>
              </div>
            ))
          )}
        </Card>

        {/* -------------------------------------------------- needs attention */}
        <Card title={t('needsAttention')}>
          {data.needs_attention.length === 0 ? (
            <Empty>{t('allMoving')}</Empty>
          ) : (
            data.needs_attention.map((d) => (
              <div className="row row-click" key={d.id} onClick={() => onOpenDeal(d.id)}>
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    background: d.idle_days > 21 ? 'var(--danger)' : 'var(--warning)',
                  }}
                />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span className="row-title" style={{ display: 'block' }}>
                    {d.title}
                  </span>
                  <span style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
                    {d.company_name} · {d.stage}
                  </span>
                </span>
                <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--warning)' }}>
                  {t('idle', { n: d.idle_days })}
                </span>
              </div>
            ))
          )}
        </Card>

        {/* ------------------------------------------------------- servicing */}
        <Card title={t('servicingDue')}>
          {data.servicing_due.length === 0 ? (
            <Empty>{t('noServicing')}</Empty>
          ) : (
            data.servicing_due.map((c) => (
              <div className="row row-click" key={c.id} onClick={() => onOpenClient(c.id)}>
                <EntityGlyph entity={c.entity_tag} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span className="row-title" style={{ display: 'block' }}>
                    {c.name}
                  </span>
                  <span style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
                    {c.next_action || t('lastActivity') + ' ' + relTime(c.last_activity_at, lang)}
                  </span>
                </span>
              </div>
            ))
          )}
        </Card>

        {/* ----------------------------------------------------------- tasks */}
        <Card
          title={t('myTasks')}
          action={
            <button className="btn btn-sm" onClick={onQuickLog}>
              {t('quickLog')}
            </button>
          }
        >
          {data.tasks.length === 0 ? (
            <Empty>{t('tasksClear')}</Empty>
          ) : (
            data.tasks.map((task) => (
              <div className="row" key={task.id}>
                <input
                  type="checkbox"
                  aria-label={task.title}
                  onChange={() => completeTask(task.id)}
                  style={{ width: 16, height: 16, accentColor: 'var(--primary)', cursor: 'pointer' }}
                />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span className="row-title" style={{ display: 'block' }}>
                    {task.title}
                  </span>
                  {task.linked_label && (
                    <span style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
                      {task.linked_label}
                    </span>
                  )}
                </span>
                <span
                  style={{
                    fontSize: 11.5,
                    fontWeight: 700,
                    color:
                      task.due_date && task.due_date < data.today
                        ? 'var(--danger)'
                        : 'var(--ink-3)',
                  }}
                >
                  {shortDate(task.due_date, lang)}
                </span>
              </div>
            ))
          )}
        </Card>
      </div>
    </div>
  );
}

const Legend = ({ color, label, value }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--ink-2)' }}>
    <span style={{ width: 8, height: 8, borderRadius: 2, background: color }} />
    {label} <strong className="num">{value}</strong>
  </span>
);
